/* ═══════════════════════════════════════════════════════════════════════════
   Menu print layout — the data document behind MenuPrint and the Designer.

   Everything that used to be hardcoded in MenuPrint (page groupings, per-
   section layout types, fixed text blocks, decoration slots) lives here as
   data, plus sparse style overrides at three scopes: theme (all), section,
   item. The saved document is the whole thing; "reset" restores DEFAULT.

   Units: font sizes in pt, spacing/dimensions in inches, letter-spacing in
   em, rotation in degrees.
   ═══════════════════════════════════════════════════════════════════════════ */

import swirl from "images/MenuPrintSwirl.svg";
import corner from "images/MenuPrintCorner.svg";
import ornament from "images/MenuPrintOrnament.png";
import logo from "images/MenuPrintLogo.png";

export const LAYOUT_VERSION = 1;
export const PX_PER_IN = 96;
export const PAGE_W = 8.5;
export const PAGE_H = 11;

export const MENU_ORDER = ["cover", "wine", "dinner", "lunch"];
export const MENU_LABELS = {
  cover: "Cover",
  wine: "Wine & Beer",
  dinner: "Dinner",
  lunch: "Lunch",
};

export const FONT_CHOICES = [
  {
    key: "body",
    label: "Cormorant Garamond",
    css: '"Cormorant Garamond", "Cormorant", "EB Garamond", "Garamond", Georgia, serif',
  },
  {
    key: "script",
    label: "Snell Roundhand (script)",
    css: '"Snell Roundhand", "Apple Chancery", "Segoe Script", cursive',
  },
  {
    key: "garamond",
    label: "EB Garamond",
    css: '"EB Garamond", "Cormorant Garamond", Georgia, serif',
  },
  {
    key: "sans",
    label: "Avenir (sans)",
    css: '"Avenir Next", "Avenir", "Futura", "Helvetica Neue", sans-serif',
  },
  {
    key: "didone",
    label: "Didot / Bodoni",
    css: '"Didot", "Bodoni 72", "Bodoni MT", "Playfair Display", serif',
  },
];

export function fontCss(key) {
  if (!key) return undefined;
  const f = FONT_CHOICES.find((c) => c.key === key);
  return f ? f.css : key;
}

export const LAYOUT_TYPES = [
  { key: "centered", label: "Centered stack", menus: ["dinner", "lunch", "wine"] },
  { key: "list", label: "Left-aligned list", menus: ["dinner", "lunch", "wine"] },
  { key: "inline", label: "Inline lines (name • price)", menus: ["dinner", "lunch", "wine"] },
  { key: "wine", label: "Beverage list", menus: ["wine", "dinner", "lunch"] },
  { key: "beer", label: "Beer lines (grouped by price)", menus: ["wine", "dinner", "lunch"] },
  { key: "lunchCentered", label: "Lunch centered (desc • price)", menus: ["lunch", "dinner"] },
  { key: "lunchGrid", label: "Two-column grid", menus: ["lunch", "dinner", "wine"] },
  { key: "lunchNames", label: "Names on one line", menus: ["lunch", "dinner", "wine"] },
  { key: "lunchClock", label: "Clock face", menus: ["lunch", "dinner"] },
  { key: "lunchRows", label: "Lunch rows", menus: ["lunch", "dinner"] },
];

export const DISTRIBUTIONS = [
  { key: "space-evenly", label: "Spread evenly" },
  { key: "space-between", label: "Push to edges" },
  { key: "space-around", label: "Spread (around)" },
  { key: "flex-start", label: "Stack from top" },
  { key: "center", label: "Center vertically" },
  { key: "flex-end", label: "Stack from bottom" },
];

export const AFTER_BLOCKS = [
  { key: "", label: "None" },
  { key: "byo", label: "Build Your Own Pizza" },
  { key: "pastaMods", label: "Make It Your Own" },
];

export const CORNER_MODES = [
  { key: "none", label: "None" },
  { key: "left", label: "Outer left (tl + bl)" },
  { key: "right", label: "Outer right (tr + br)" },
  { key: "all", label: "All four" },
];

export const MASTHEADS = [
  { key: "", label: "None" },
  { key: "bev", label: "Wine & Beer title" },
  { key: "lunch", label: "Lunch Specials" },
];

// Built-in decoration artwork. Uploaded images are referenced as "a:<id>".
export const BUILTIN_ASSETS = [
  { key: "swirl", label: "Title swirl", src: swirl },
  { key: "corner", label: "Corner flourish", src: corner },
  { key: "ornament", label: "Masthead ornament", src: ornament },
  { key: "logo", label: "Trattoria Demi logo", src: logo },
];

export function assetSrc(ref, assets) {
  if (!ref) return null;
  const b = BUILTIN_ASSETS.find((a) => a.key === ref);
  if (b) return b.src;
  if (String(ref).startsWith("a:")) {
    const id = ref.slice(2);
    const a = (assets || []).find((x) => x._id === id);
    return a ? a.dataUrl : null;
  }
  return null;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

export function norm(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();
}

/* "PASTA#2" → base "PASTA", part 2. */
export function splitKey(key) {
  const m = /^(.*)#(\d+)$/.exec(key || "");
  return m ? { base: m[1], part: Number(m[2]) } : { base: key, part: 1 };
}

export function uid(prefix = "e") {
  return `${prefix}${Math.random().toString(36).slice(2, 9)}${Date.now()
    .toString(36)
    .slice(-3)}`;
}

export function deepClone(v) {
  return JSON.parse(JSON.stringify(v));
}

export function getAt(obj, path) {
  return path.reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

/* Immutable set: returns a new root with `path` set to value (arrays kept). */
export function setAt(root, path, value) {
  if (!path.length) return value;
  const [k, ...rest] = path;
  const isIndex = typeof k === "number";
  const copy = Array.isArray(root) ? [...root] : { ...(root || {}) };
  if (isIndex && !Array.isArray(root)) return copy;
  copy[k] = setAt(root ? root[k] : undefined, rest, value);
  return copy;
}

/* Merge a patch into the object at path (shallow), dropping undefined keys. */
export function patchAt(root, path, patch) {
  const cur = getAt(root, path) || {};
  const next = { ...cur };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined || v === null || v === "") delete next[k];
    else next[k] = v;
  }
  return setAt(root, path, next);
}

// ─── Defaults ──────────────────────────────────────────────────────────────

export const DEFAULT_LAYOUT = {
  version: LAYOUT_VERSION,
  theme: {
    font: "body",
    scriptFont: "script",
    ink: "#111111",
    paper: "#ffffff",
    page: {},
    section: {},
    sectionTitle: {},
    sectionSub: {},
    item: {},
    itemName: {},
    itemDesc: {},
    itemPrice: {},
    flourish: {
      asset: "swirl",
      hidden: false,
      height: 0.19,
      rotate: -10,
      gap: 0.17,
      dy: 0.02,
      mirror: true,
      opacity: 1,
    },
    corner: {
      asset: "corner",
      hidden: false,
      size: 0.55,
      inset: 0.35,
      rotate: 0,
      opacity: 1,
    },
    gfMark: { hidden: false, size: 0.72, color: "#3a3a3a" },
  },
  cover: {
    hidden: false,
    page: {},
    free: [],
    logo: { asset: "logo", width: 6.2, marginTop: 2.6 },
  },
  text: {
    established: { value: "Est. 1993" },
    footerNote: {
      value: "No substitutions. 20% gratuity will be added to parties of 6+.",
    },
    beverageTitle: { value: "Wine & Beer" },
    beverageSub: { value: "glass • bottle" },
    lunchTitle: { value: "Lunch Specials" },
    lunchNote: { value: "served daily until 4 pm · excluding holidays" },
    bestSeller: { value: "ASIAGO STUFFED GNOCCHI" },
    bestSellerLabel: { value: "#1 best seller" },
    byoTitle: { value: "BUILD YOUR OWN PIZZA" },
    byoSubtitle: { value: "thin crust with tomato sauce and mozzarella base" },
    byoRows: {
      value:
        "9” cheese $12.00 additional ingredient $3.50\n12” cheese $14.00 additional ingredient $4.00\n14” cheese $17.00 additional ingredient $4.50",
    },
    byoToppings: {
      value:
        "besciamella, parmesan, goat cheese, anchovies, mushrooms, black or green olives, sausage, pepperoni, garlic, chicken, green or red peppers, broccoli, spinach, onions, fresh tomato",
    },
    modsTitle: { value: "MAKE IT YOUR OWN" },
    modsRows: {
      value:
        "add chicken 4.00 • add salmon 6.00\nsubstitute whole wheat or gluten free pasta 2.00\nsauce and pasta substitutions available upon request",
    },
  },
  menus: {
    wine: {
      pages: [
        { id: "w1", sections: ["BEER", "BUBBLY", "WHITE"], masthead: "bev", corners: "left", free: [], page: {} },
        { id: "w2", sections: ["ROSE", "RED"], masthead: "bev", corners: "right", free: [], page: {} },
      ],
    },
    dinner: {
      pages: [
        { id: "d1", sections: ["SMALL PLATES", "FOR THE TABLE"], free: [], page: {} },
        { id: "d2", sections: ["SALADS", "DRINKS"], free: [], page: {} },
        { id: "d3", sections: ["PASTA"], free: [], page: { distribute: "flex-start", paddingTop: 0.9 } },
        { id: "d4", sections: ["PASTA#2"], free: [], page: { distribute: "flex-start", paddingTop: 0.9 } },
        { id: "d5", sections: ["ENTREES", "SEAFOOD"], free: [], page: {} },
        { id: "d6", sections: ["PIZZA"], free: [], page: {} },
        { id: "d7", sections: ["SIDES", "DESSERTS"], free: [], page: {} },
      ],
    },
    lunch: {
      pages: [
        { id: "l1", sections: ["SPECIALTY HOT SANDWICHES", "SPECIALTY COLD SANDWICHES"], masthead: "lunch", corners: "left", free: [], page: {} },
        { id: "l2", sections: ["PETITE SANDWICHES", "PASTA SPECIALS", "OTHER LUNCH SPECIALS"], masthead: "lunch", corners: "right", free: [], page: { sectionGap: 0.8 } },
      ],
    },
  },
  sections: {
    PASTA: {
      layout: "centered",
      airy: true,
      gfTags: true,
      note: "whole wheat and GF available",
      parts: 2,
    },
    "PASTA#2": { title: "PASTA", after: "pastaMods" },
    ENTREES: { note: "all entrées can be prepared gluten free" },
    SEAFOOD: { note: "all seafood can be prepared gluten free" },
    PIZZA: { layout: "centered", title: "SPECIALTY PIZZA", showSizes: true, after: "byo" },
    SIDES: { layout: "inline" },
    DRINKS: { layout: "inline", sodaFountain: true },
    BEER: { layout: "beer" },
    DESSERTS: { title: "DESSERT" },
    "PETITE SANDWICHES": { layout: "lunchNames", title: '3" PETITE SANDWICHES' },
    "PASTA SPECIALS": { layout: "lunchGrid", tight: true },
    "OTHER LUNCH SPECIALS": { layout: "lunchClock" },
  },
  items: {},
};

export const DEFAULT_LAYOUT_BY_MENU = {
  dinner: "centered",
  lunch: "lunchCentered",
  wine: "wine",
};

// ─── Resolution ────────────────────────────────────────────────────────────

/* Load: take the saved document (or defaults) and make sure every CMS
   section has a place on some page, so nothing silently drops out of the
   print. New sections land on a fresh trailing page of their own menu. */
export function resolveLayout(saved, menus) {
  let layout = saved && typeof saved === "object" ? deepClone(saved) : deepClone(DEFAULT_LAYOUT);
  // Fill any top-level branches a saved doc might be missing.
  for (const k of Object.keys(DEFAULT_LAYOUT))
    if (layout[k] === undefined) layout[k] = deepClone(DEFAULT_LAYOUT[k]);
  for (const k of Object.keys(DEFAULT_LAYOUT.text))
    if (!layout.text[k]) layout.text[k] = deepClone(DEFAULT_LAYOUT.text[k]);
  for (const k of Object.keys(DEFAULT_LAYOUT.theme))
    if (layout.theme[k] === undefined) layout.theme[k] = deepClone(DEFAULT_LAYOUT.theme[k]);
  for (const m of ["wine", "dinner", "lunch"]) {
    if (!layout.menus[m]) layout.menus[m] = { pages: [] };
    layout.menus[m].pages = (layout.menus[m].pages || []).map((p) => ({
      id: p.id || uid("p"),
      sections: p.sections || [],
      free: p.free || [],
      page: p.page || {},
      ...p,
    }));
  }
  if (!menus) return layout;

  const placed = new Set();
  for (const m of Object.values(layout.menus))
    for (const p of m.pages) for (const k of p.sections) placed.add(k);

  for (const type of ["wine", "dinner", "lunch"]) {
    for (const sec of menus[type]?.sections || []) {
      const key = norm(sec.header);
      const parts = layout.sections[key]?.parts || 1;
      const keys = [key];
      for (let i = 2; i <= parts; i++) keys.push(`${key}#${i}`);
      for (const k of keys) {
        if (placed.has(k)) continue;
        placed.add(k);
        layout.menus[type].pages.push({
          id: uid("p"),
          sections: [k],
          free: [],
          page: {},
          ...(type === "lunch" ? { masthead: "lunch", corners: "left" } : {}),
          ...(type === "wine" ? { masthead: "bev", corners: "left" } : {}),
        });
      }
    }
  }
  return layout;
}

/* Section config: defaults by menu type, then the base section, then the
   part-specific overrides (PASTA#2). Style sub-objects merge shallowly. */
const STYLE_KINDS = ["section", "sectionTitle", "sectionSub", "item", "itemName", "itemDesc", "itemPrice", "flourish"];

export function sectionConfig(layout, key, menuType) {
  const { base } = splitKey(key);
  const a = layout.sections?.[base] || {};
  const b = key !== base ? layout.sections?.[key] || {} : {};
  const cfg = { layout: DEFAULT_LAYOUT_BY_MENU[menuType] || "centered", ...a, ...b };
  for (const k of STYLE_KINDS)
    if (a[k] || b[k]) cfg[k] = { ...(a[k] || {}), ...(b[k] || {}) };
  return cfg;
}

/* Effective sparse style for a node kind at a given scope chain. */
export function resolveStyle(layout, kind, { sectionKey, itemId } = {}) {
  const out = { ...(layout.theme?.[kind] || {}) };
  if (sectionKey) {
    const { base } = splitKey(sectionKey);
    Object.assign(out, layout.sections?.[base]?.[kind] || {});
    if (base !== sectionKey) Object.assign(out, layout.sections?.[sectionKey]?.[kind] || {});
  }
  if (itemId) Object.assign(out, layout.items?.[itemId]?.[kind] || {});
  return out;
}

// ─── Style → CSS ───────────────────────────────────────────────────────────

const inch = (v) => (v == null ? undefined : `${v}in`);
const pt = (v) => (v == null ? undefined : `${v}pt`);

/* Sparse style object → inline React style. Only set keys are emitted, so
   the print CSS keeps supplying every default. */
export function toCss(s) {
  if (!s) return undefined;
  const css = {};
  if (s.fontSize != null) css.fontSize = pt(s.fontSize);
  if (s.letterSpacing != null) css.letterSpacing = `${s.letterSpacing}em`;
  if (s.lineHeight != null) css.lineHeight = s.lineHeight;
  if (s.fontWeight != null) css.fontWeight = s.fontWeight;
  if (s.fontStyle) css.fontStyle = s.fontStyle;
  if (s.textTransform) css.textTransform = s.textTransform;
  if (s.textAlign) css.textAlign = s.textAlign;
  if (s.color) {
    css.color = s.color;
    css["--mp-ink"] = s.color;
  }
  if (s.font) css["--mp-font"] = fontCss(s.font);
  if (s.marginTop != null) css.marginTop = inch(s.marginTop);
  if (s.marginBottom != null) css.marginBottom = inch(s.marginBottom);
  if (s.marginLeft != null) css.marginLeft = inch(s.marginLeft);
  if (s.marginRight != null) css.marginRight = inch(s.marginRight);
  if (s.paddingTop != null) css.paddingTop = inch(s.paddingTop);
  if (s.paddingBottom != null) css.paddingBottom = inch(s.paddingBottom);
  if (s.paddingLeft != null) css.paddingLeft = inch(s.paddingLeft);
  if (s.paddingRight != null) css.paddingRight = inch(s.paddingRight);
  if (s.padding != null) css.padding = inch(s.padding);
  if (s.gap != null) css.gap = inch(s.gap);
  if (s.maxWidth != null) css.maxWidth = inch(s.maxWidth);
  if (s.width != null) css.width = inch(s.width);
  if (s.height != null) css.height = inch(s.height);
  if (s.opacity != null) css.opacity = s.opacity;
  if (s.background) css.background = s.background;
  const t = [];
  if (s.offsetX || s.offsetY) t.push(`translate(${s.offsetX || 0}in, ${s.offsetY || 0}in)`);
  if (s.rotate) t.push(`rotate(${s.rotate}deg)`);
  if (t.length) css.transform = t.join(" ");
  if (s.distribute) css.justifyContent = s.distribute;
  return Object.keys(css).length ? css : undefined;
}

/* Where a section key currently sits: { menu, pageIndex } or null. */
export function locateSection(layout, key) {
  for (const [menu, m] of Object.entries(layout.menus || {})) {
    const i = (m.pages || []).findIndex((p) => (p.sections || []).includes(key));
    if (i >= 0) return { menu, pageIndex: i };
  }
  return null;
}

/* Text keys → display labels for the inspector breadcrumb. */
export const TEXT_LABELS = {
  established: "Cover · Est. line",
  footerNote: "Cover · footer note",
  beverageTitle: "Wine & Beer title",
  beverageSub: "Wine & Beer subtitle",
  lunchTitle: "Lunch title",
  lunchNote: "Lunch service line",
  bestSeller: "Best-seller item",
  bestSellerLabel: "Best-seller label",
  byoTitle: "Build Your Own · title",
  byoSubtitle: "Build Your Own · subtitle",
  byoRows: "Build Your Own · price rows",
  byoToppings: "Build Your Own · toppings",
  modsTitle: "Make It Your Own · title",
  modsRows: "Make It Your Own · rows",
};

// ─── Free elements (the Canva-style layer) ─────────────────────────────────
// Positions and sizes are stored in inches (print-native, floating point
// preserved as-is). The inspector converts to px (96/in) or % of the page.

export const FREE_TYPES = [
  { key: "text", label: "Text" },
  { key: "image", label: "Image" },
  { key: "rect", label: "Rectangle" },
  { key: "ellipse", label: "Ellipse" },
  { key: "rule", label: "Line" },
];

export const GRID_STEP = 0.125; // in — 1/8" grid
export const MIN_SIZE = 0.1; // in
export const MAX_ZOOM = 3;
export const MIN_ZOOM = 0.25;

const TYPE_NAMES = { text: "Text", image: "Image", rect: "Rectangle", ellipse: "Ellipse", rule: "Line" };

/* Next free name of the form "Text 3" across every page. */
export function nextFreeName(layout, type) {
  const base = TYPE_NAMES[type] || "Element";
  let max = 0;
  const scan = (list) =>
    (list || []).forEach((e) => {
      const m = new RegExp(`^${base} (\\d+)$`).exec(e.name || "");
      if (m) max = Math.max(max, Number(m[1]));
    });
  scan(layout.cover?.free);
  for (const m of Object.values(layout.menus || {})) for (const p of m.pages || []) scan(p.free);
  return `${base} ${max + 1}`;
}

export function newFreeElement(layout, type) {
  const id = uid("f");
  const name = nextFreeName(layout, type);
  const base = { id, name, type, x: 0, y: 0, w: 2, h: 1, rotate: 0, opacity: 1, locked: false, hidden: false, flipH: false, flipV: false };
  switch (type) {
    case "image":
      return { ...base, asset: "ornament", w: 3, h: 1, x: (PAGE_W - 3) / 2, y: 4.8, lockAspect: true };
    case "text":
      return { ...base, text: "Your text here", w: 4, h: 0.5, x: (PAGE_W - 4) / 2, y: 5.2, style: { fontSize: 14, textAlign: "center" }, overflow: "wrap" };
    case "rect":
      return { ...base, w: 2, h: 1.2, x: (PAGE_W - 2) / 2, y: 5, fill: "#00000000", border: { width: 0.75, color: "#111111", style: "solid" } };
    case "ellipse":
      return { ...base, w: 1.5, h: 1.5, x: (PAGE_W - 1.5) / 2, y: 5, fill: "#00000000", border: { width: 0.75, color: "#111111", style: "solid" } };
    default:
      return { ...base, w: 3, h: 0, x: (PAGE_W - 3) / 2, y: 5.5, thickness: 0.75, color: "#111111" };
  }
}

/* Duplicate with a fresh id and an incremented name, offset a touch. */
export function cloneFreeElement(layout, el) {
  const type = el.type;
  return { ...deepClone(el), id: uid("f"), name: nextFreeName(layout, type), x: el.x + 0.25, y: el.y + 0.25 };
}

export const UNITS = [
  { key: "in", label: "in" },
  { key: "px", label: "px" },
  { key: "pct", label: "%" },
];

/* Inches ↔ display unit. `axis` picks page width or height for %. */
export function fromIn(v, unit, axis = "x") {
  if (v == null) return v;
  if (unit === "px") return Math.round(v * PX_PER_IN * 100) / 100;
  if (unit === "pct") return Math.round((v / (axis === "x" ? PAGE_W : PAGE_H)) * 10000) / 100;
  return Math.round(v * 1000) / 1000;
}

export function toIn(v, unit, axis = "x") {
  if (v == null || !Number.isFinite(Number(v))) return undefined;
  const n = Number(v);
  if (unit === "px") return n / PX_PER_IN;
  if (unit === "pct") return (n / 100) * (axis === "x" ? PAGE_W : PAGE_H);
  return n;
}

export function snapToGrid(v, step = GRID_STEP) {
  return Math.round(v / step) * step;
}
